import { getAuthConfig } from '../config/auth-config';

const COOKIE_KEY_ID_TOKEN = 'id_token';
const COOKIE_KEY_OPENID_TOKEN = 'openid_token';
let _redirecting = false;

export async function authenticateWithCognito(): Promise<void> {
  if (hasValidTokens()) {
    return;
  }
  if (_redirecting) {
    // Already redirecting to Cognito — don't start another
    return new Promise(() => {});
  }

  clearTokens();

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');

  if (!code || !state) {
    _redirecting = true;
    await redirectToLogin();
  } else if (sessionStorage.getItem('pkce_state') !== state) {
    _redirecting = true;
    await redirectToLogin();
  } else {
    await exchangeCodeForTokens(code);
    urlParams.delete('code');
    urlParams.delete('state');
    
    // Clean up the URL by removing the query parameters
    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
    window.history.replaceState({}, document.title, newUrl);
  }
}

function hasValidTokens(): boolean {
  const openIdToken = getCookie(COOKIE_KEY_OPENID_TOKEN);
  if (!openIdToken) return false;
  
  const claims = parseJwtClaims(openIdToken);
  const now = Math.floor(Date.now() / 1000);
  return claims.exp > now;
}

async function redirectToLogin(): Promise<void> {
  const codeVerifier = generateRandomString();
  sessionStorage.setItem('code_verifier', codeVerifier);

  const arrayHash = await encryptStringWithSHA256(codeVerifier);
  const codeChallenge = hashToBase64Url(arrayHash);
  sessionStorage.setItem('code_challenge', codeChallenge);

  const state = generateRandomString();
  sessionStorage.setItem('pkce_state', state);

  const config = getAuthConfig();
  const authUrl = `https://${config.cognitoDomain}/oauth2/authorize?` +
    `response_type=code&` +
    `state=${state}&` +
    `client_id=${config.userPoolClientId}&` +
    `redirect_uri=${encodeURIComponent(config.redirectUri)}&` +
    `scope=openid&` +
    `code_challenge_method=S256&` +
    `code_challenge=${codeChallenge}`;

  window.location.href = authUrl;
}

async function exchangeCodeForTokens(code: string): Promise<void> {
  const codeVerifier = sessionStorage.getItem('code_verifier');
  const config = getAuthConfig();

  const response = await fetch(
    `https://${config.cognitoDomain}/oauth2/token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.userPoolClientId,
        code_verifier: codeVerifier!,
        redirect_uri: config.redirectUri,
        code,
      }),
    }
  );

  const data = await response.json();
  if (data.id_token) {
    setCookie(COOKIE_KEY_OPENID_TOKEN, data.id_token);
    const claims = parseJwtClaims(data.id_token);
    setCookie(COOKIE_KEY_ID_TOKEN, JSON.stringify(claims));
  }
}

function generateRandomString(): string {
  const array = new Uint32Array(28);
  crypto.getRandomValues(array);
  return Array.from(array).map(n => `${(n / 100) % 100}`).join('');
}

function encryptStringWithSHA256(str: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  return crypto.subtle.digest('SHA-256', data);
}

function hashToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const binary = String.fromCharCode(...bytes);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function parseJwtClaims(token: string): any {
  const parts = token.split('.');
  return JSON.parse(atob(parts[1]));
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : undefined;
}

function setCookie(name: string, value: string): void {
  document.cookie = `${name}=${value}; path=/; secure; samesite=strict`;
}

function clearTokens(): void {
  document.cookie = `${COOKIE_KEY_ID_TOKEN}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  document.cookie = `${COOKIE_KEY_OPENID_TOKEN}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

export function getOpenIdToken(): string | undefined {
  return getCookie(COOKIE_KEY_OPENID_TOKEN);
}

export function getUserAlias(): string | undefined {
  const openIdToken = getOpenIdToken();
  if (!openIdToken) return undefined;
  
  const claims = parseJwtClaims(openIdToken);
  // The 'sub' claim contains the Amazon alias when using Federate
  return claims.sub || claims.username;
}
